import { SharePointListClient, type ListItem } from '../SharePointListClient';

/**
 * `99.ABM_MediosDePago` — catalog of payment methods. Each row drives one
 * line in the Pagos screen's medios de pago table (Efectivo Pesos,
 * Transferencia Galicia, MercadoPago, …).
 *
 * Field semantics:
 * - `Categoria_MDP` is 'Local' (ARS-denominated) or 'Internacional' (USD).
 *   When the venta's moneda differs from the medio's categoria, the row
 *   needs a `TipoCambio_P` on write.
 * - `Tipo_MDP` classifies the medio (Efectivo / Billetera Virtual / Banco).
 *   The Pagos dialog uses it to drive the auto-`Lleva Factura` rule.
 * - `LlevaFactura_MDP` ('SI'|'NO') forces the Lleva-Factura toggle ON
 *   when set. Set per-row from the Configuración CRUD: typically
 *   true for banks (default), false for Efectivo / Billetera Virtual,
 *   and configurable for the Morvalab exception.
 */
export type MediosDePagoFields = {
  Title?: string | null;
  Medio_MDP?: string | null;
  Categoria_MDP?: 'Local' | 'Internacional' | string | null;
  Cuotas_MDP?: string | null;
  Status_MDP?: 'Activo' | 'Inactivo' | string | null;
  Tipo_MDP?: 'Efectivo' | 'Billetera Virtual' | 'Banco' | 'E-cheq' | string | null;
  LlevaFactura_MDP?: 'SI' | 'NO' | string | null;
  /** Caja interna de Tesorería: no se ofrece en los cobros de ventas. */
  SoloTesoreria_MDP?: 'SI' | 'NO' | string | null;
};

export type MedioDePagoItem = ListItem<MediosDePagoFields>;

export const mediosDePagoClient = new SharePointListClient<MediosDePagoFields>(
  'ABM_MediosDePago',
);

/**
 * List every active medio de pago. `Status_MDP` isn't indexed on this
 * list so we fetch the whole (small) catalog and filter client-side —
 * avoids the "not indexed" Graph error we hit with Campanias orderBy.
 */
export async function listActiveMediosDePago(): Promise<MedioDePagoItem[]> {
  const items = await mediosDePagoClient.listItems({ top: 500 });
  return items
    .filter((m) => m.fields.Status_MDP === 'Activo')
    .sort((a, b) =>
      (a.fields.Medio_MDP ?? '').localeCompare(b.fields.Medio_MDP ?? ''),
    );
}

/** All medios (active + inactive). Drives the Configuración CRUD list. */
export async function listAllMediosDePago(): Promise<MedioDePagoItem[]> {
  const items = await mediosDePagoClient.listItems({ top: 500 });
  return items.sort((a, b) =>
    (a.fields.Medio_MDP ?? '').localeCompare(b.fields.Medio_MDP ?? ''),
  );
}

export type MedioDePagoPayload = {
  medio: string;
  categoria: 'Local' | 'Internacional' | null;
  tipo: 'Efectivo' | 'Billetera Virtual' | 'Banco' | 'E-cheq' | null;
  llevaFactura: boolean;
  soloTesoreria: boolean;
  cuotas?: string | null;
};

function payloadToFields(p: MedioDePagoPayload): Partial<MediosDePagoFields> {
  return {
    Medio_MDP: p.medio.trim(),
    Categoria_MDP: p.categoria ?? undefined,
    Tipo_MDP: p.tipo ?? undefined,
    LlevaFactura_MDP: p.llevaFactura ? 'SI' : 'NO',
    SoloTesoreria_MDP: p.soloTesoreria ? 'SI' : 'NO',
    Cuotas_MDP: p.cuotas?.trim() || undefined,
  };
}

export async function createMedioDePago(
  payload: MedioDePagoPayload,
): Promise<MedioDePagoItem> {
  return mediosDePagoClient.createItem({
    Title: '[sumar]',
    ...payloadToFields(payload),
    Status_MDP: 'Activo',
  });
}

export async function updateMedioDePago(
  id: string,
  payload: MedioDePagoPayload,
): Promise<MedioDePagoItem> {
  return mediosDePagoClient.updateItem(id, payloadToFields(payload));
}

export async function softDeleteMedioDePago(id: string): Promise<MedioDePagoItem> {
  return mediosDePagoClient.updateItem(id, { Status_MDP: 'Inactivo' });
}

export async function reactivateMedioDePago(id: string): Promise<MedioDePagoItem> {
  return mediosDePagoClient.updateItem(id, { Status_MDP: 'Activo' });
}
